<template>
	<div class="voltage-monitor-page">
		<section class="hero-panel">
			<div class="hero-panel__glow hero-panel__glow--left"></div>
			<div class="hero-panel__glow hero-panel__glow--right"></div>

			<div class="page-header">
				<div class="page-header__title">
					<h2 class="page-title">调压器监控</h2>
					<p class="page-subtitle">近 7 日监测概览、告警态势与异常设备联动视图</p>
				</div>
				<div class="header-actions">
					<span class="date-label">起始日期</span>
					<el-date-picker
						v-model="monitorDate"
						type="date"
						placeholder="选择日期"
						value-format="yyyy-MM-dd"
						format="yyyy-MM-dd"
						size="small"
						prefix-icon="el-icon-time"
						style="width: 180px"
					/>
				</div>
			</div>
		</section>

		<StatsCardRow :time-range="timeRange" class="card-row" />

		<div class="main-content">
			<MapSection class="map-section" />
			<AlarmList class="alarm-section" :time-range="timeRange" />
		</div>

		<DeviceTable class="device-table" :time-range="timeRange" />
	</div>
</template>

<script>
import StatsCardRow from './components/StatsCardRow.vue'
import MapSection from './components/MapSection.vue'
import AlarmList from './components/AlarmList.vue'
import DeviceTable from './components/DeviceTable.vue'

export default {
	name: 'VoltageMonitor',
	components: { StatsCardRow, MapSection, AlarmList, DeviceTable },
	data() {
		return {
			monitorDate: this.dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
		}
	},
	computed: {
		timeRange() {
			return [
				this.dayjs(this.monitorDate).startOf('day').toDate(),
				this.dayjs(this.monitorDate).endOf('day').toDate(),
			]
		},
	},
}
</script>

<style lang="scss" scoped>
.voltage-monitor-page {
	--page-bg: #eef4f8;
	--panel-bg: rgba(255, 255, 255, 0.96);
	--panel-border: #dbe5ee;
	--panel-shadow: 0 18px 38px rgba(15, 23, 42, 0.05);
	--title: #0f172a;
	--text: #334155;
	--muted: #64748b;
	--line: #e2e8f0;
	--primary: #2563eb;
	--primary-soft: #eff6ff;

	padding: 12px;
	display: flex;
	flex-direction: column;
	gap: 12px;
	height: 100%;
	overflow-y: auto;
	background: radial-gradient(circle at top right, rgba(59, 130, 246, 0.12), transparent 26%),
		radial-gradient(circle at top left, rgba(14, 165, 233, 0.14), transparent 20%),
		linear-gradient(180deg, #f5f9fc 0%, var(--page-bg) 100%);
	font-family: 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
	color: var(--text);
}

.voltage-monitor-page > * {
	flex-shrink: 0;
}

.hero-panel {
	position: relative;
	overflow: hidden;
	padding: 24px 28px 22px;
	border: 1px solid var(--panel-border);
	border-radius: 12px;
	box-shadow: var(--panel-shadow);
	backdrop-filter: blur(12px);
	background: linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(248, 251, 255, 0.94)), var(--panel-bg);
}

.hero-panel__glow {
	position: absolute;
	width: 280px;
	height: 280px;
	border-radius: 50%;
	filter: blur(18px);
	opacity: 0.25;
	pointer-events: none;
}

.hero-panel__glow--left {
	left: -120px;
	top: -120px;
	background: rgba(37, 99, 235, 0.22);
}

.hero-panel__glow--right {
	right: -100px;
	top: -120px;
	background: rgba(14, 165, 233, 0.22);
}

.page-header {
	position: relative;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
}

.page-header__title {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.page-title {
	margin: 0;
	font-size: 22px;
	font-weight: 700;
	letter-spacing: 0.01em;
	color: var(--title);
}

.page-subtitle {
	margin: 0;
	font-size: 14px;
	line-height: 1.6;
	color: var(--muted);
}

.header-actions {
	display: flex;
	align-items: center;
	gap: 8px;
}

.date-label {
	font-size: 14px;
	color: var(--text);
	white-space: nowrap;
}

.header-actions ::v-deep .el-range-editor.el-input__inner {
	display: inline-flex;
	align-items: center;
	height: 38px;
	border-radius: 10px;
	border: 1px solid #d7dee8;
	background: rgba(255, 255, 255, 0.92);
	box-shadow: none;
}

.header-actions ::v-deep .el-range-editor.is-active,
.header-actions ::v-deep .el-range-editor.is-active:hover,
.header-actions ::v-deep .el-range-editor:hover {
	border-color: #bfdbfe;
}

.header-actions ::v-deep .el-range-input,
.header-actions ::v-deep .el-range-separator,
.header-actions ::v-deep .el-input__icon {
	color: #64748b;
	height: auto;
	line-height: 1;
	display: inline-flex;
	align-items: center;
	justify-content: center;
}

.header-actions ::v-deep .el-date-editor .el-input__inner {
	height: 38px;
	line-height: 38px;
	border-radius: 10px;
	border: 1px solid #d7dee8;
}

.header-actions ::v-deep .el-date-editor .el-input__prefix {
	display: flex;
	align-items: center;
	height: 100%;
}

.header-actions ::v-deep .el-date-editor .el-input__suffix {
	display: flex;
	align-items: center;
	height: 100%;
}

.header-actions ::v-deep .el-date-editor .el-input__icon {
	height: 100%;
	line-height: 1;
}

.header-actions ::v-deep .el-range-input {
	flex: 1;
}

.header-actions ::v-deep .el-range-separator {
	padding: 0 8px;
}

.card-row {
	margin-bottom: 0;
}

.main-content {
	display: flex;
	gap: 12px;

	.map-section {
		flex: 3;
		min-width: 0;
	}
	.alarm-section {
		flex: 2;
		min-width: 0;
	}
}
.device-table {
	flex-shrink: 0;
}

@media (max-width: 1240px) {
	.page-header,
	.main-content {
		flex-direction: column;
		align-items: stretch;
	}

	.header-actions {
		width: 100%;
	}

	.header-actions ::v-deep .el-date-editor {
		width: 100% !important;
	}
}
</style>
